import { AppliedItemTypes, ItemConditionEnum, SortOptionsEnum } from '@/constants/enum';
import { optionLanguage } from '@/constants/options';
import store from '@/redux/store';
import { IOptions } from '@/types';
import { IToast } from '@/types/components/toast';
import { IItemCondition, IRule } from '@/types/newSetup.type';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { stringify, uniq, uniqByKey } from './string';
declare var $crisp: any;

dayjs.extend(utc);
dayjs.extend(timezone);
export const openCrisp = () => {
  try {
    $crisp.push(['do', 'chat:open']);
  } catch (error) {
    console.log(error);
  }
};

export const sendCrispMessage = (message: string) => {
  try {
    $crisp.push(['do', 'chat:open']);
    $crisp.push(['do', 'message:send', ['text', message]]);
  } catch (err) {
    console.log(err);
  }
};

export function validateUrl(url: string) {
  // eslint-disable-next-line no-useless-escape
  const regex = /[(www\.)?a-zA-Z0-9@:%._\+\-~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/i;
  const result = url?.toLowerCase().match(regex);
  return !!result;
}

export function validateEmail(email: string) {
  const regex = new RegExp(/^[\w.+-]+@[a-zA-Z_-]+?(?:\.[a-zA-Z]{2,6})+$/);
  const result = email.match(regex);
  return !!result;
}

export const handleToggle = (value: number): number => (value === 0 ? 1 : 0);

export const numberToBoolean = (number: number) => {
  return !!number;
};

export const booleanToNumber = (value: boolean) => {
  return value ? 1 : 0;
};

export const checkShowErrorInline = (
  res: any,
): {
  status: boolean;
  msg: string;
} => {
  try {
    if ('data' in res) {
      return {
        status: res.data.code !== 200,
        msg: res.data.status,
      };
    } else {
      return {
        status: true,
        msg: res.error.data.message.toString() || 'something happened',
      };
    }
  } catch (err) {
    return {
      status: true,
      msg: 'something happened',
    };
  }
};

export const handleToastMutation = (res: any): IToast => {
  try {
    if ('data' in res) {
      if (res.data.code !== 200) {
        return {
          isOpen: true,
          content: res.data.message || 'Failed',
          error: true,
        };
      } else {
        return {
          isOpen: true,
          content: res.data.message || 'Saved',
          error: false,
        };
      }
    } else {
      return {
        isOpen: true,
        content: res.error.data.message.join(', ') || 'something happened',
        error: false,
      };
    }
  } catch (err) {
    return {
      isOpen: true,
      content: 'something happened',
      error: true,
    };
  }
};

export function deepObjectEqual(object1: any, object2: any) {
  return stringify(object1) === stringify(object2);
}

export function deepCompareObjectEqual(obj1: any, obj2: any) {
  // Check if both inputs are objects
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }

  // Check if both are null (since typeof null is 'object')
  if (obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }

  // Get keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if the number of keys is the same
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Recursively compare all keys and values
  for (let key of keys1) {
    if (!keys2.includes(key) || !deepCompareObjectEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Returns an array of IOptions that have all their codes selected.
 *
 * @param {string[]} selectedCodes - The list of selected codes.
 * @param {IOptions[]} options - The list of options.
 * @returns {IOptions[]} An array of multiple code options.
 */
export const getOptionsMultipleCode = (selectedCodes: string[], options: IOptions[]): IOptions[] => {
  return options
    ?.filter((option) => option.value.includes(','))
    ?.filter((option) => {
      const optionCodes = option.value.split(',');
      return optionCodes.every((code) => selectedCodes.includes(code.trim()));
    });
};

export const getOptionsSelectedAdd = (
  newValue: Array<string>,
  oldValue: Array<IOptions>,
  options: Array<IOptions>,
  key?: string,
) => {
  const optionValues = uniq(newValue.map((value) => value.trim())); // Loại bỏ khoảng trắng không cần thiết

  const selectedOptions = options?.filter((option) => optionValues.includes(option.value));

  return uniqByKey([...oldValue, ...selectedOptions], key);
};

export const getOptionsSelectedRemove = (newValue: Array<string>, oldValue: Array<IOptions>) => {
  const optionSelected = oldValue?.filter((option) => newValue.includes(option.value));
  return optionSelected;
};

export const convertStringToOptionsSelected = (value: Array<string>, options: Array<IOptions>) => {
  const currentResult: Array<IOptions> = [];
  value.forEach((item) => {
    const optionSelected = options.find((option) => option.value === item);
    if (optionSelected) currentResult.push(optionSelected);
  });
  return currentResult;
};

export const convertNullToString = (obj: any) => {
  if (obj === undefined) {
    return undefined;
  }
  const res = { ...obj };
  Object.entries(res).forEach(([key, value]) => {
    if (value === null) {
      res[key] = '';
    }
  });
  return res;
};

export const checkNavigateContextualBar = () => {
  const { checkDataSetting, checkDeliveryMethodSetting, checkZipcodeSetting } = store.getState().settings;

  return checkDataSetting || checkDeliveryMethodSetting || checkZipcodeSetting;
};

export const checkArraysEqual = (array1: any[], array2: any[]) => {
  if (array1.length !== array2.length) {
    return false;
  }

  return array1.every((item) => array2.includes(item)) && array2.every((item) => array1.includes(item));
};

export const sortRulesBy = (rules: IRule[], sortBy?: SortOptionsEnum) => {
  switch (sortBy) {
    case SortOptionsEnum.IdAsc:
      return rules.sort((a, b) => (a?.id || 0) - (b?.id || 0));
    case SortOptionsEnum.IdDesc:
      return rules.sort((a, b) => (b?.id || 0) - (a?.id || 0));
    case SortOptionsEnum.NameAsc:
      return rules.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    case SortOptionsEnum.NameDesc:
      return rules.sort((a, b) => (b?.name || '').localeCompare(a?.name || ''));
    case SortOptionsEnum.MinPrepareAsc:
      return rules.sort((a, b) => (a?.minPrepDays || 0) - (b?.minPrepDays || 0));
    case SortOptionsEnum.MinPrepareDesc:
      return rules.sort((a, b) => (b?.minPrepDays || 0) - (a?.minPrepDays || 0));
    case SortOptionsEnum.MaxPrepareAsc:
      return rules.sort((a, b) => (a?.maxPrepDays || 0) - (b?.maxPrepDays || 0));
    case SortOptionsEnum.MaxPrepareDesc:
      return rules.sort((a, b) => (b?.maxPrepDays || 0) - (a?.maxPrepDays || 0));
    case SortOptionsEnum.MinDeliveryAsc:
      return rules.sort((a, b) => (a?.minDeliveryDays || 0) - (b?.minDeliveryDays || 0));
    case SortOptionsEnum.MinDeliveryDesc:
      return rules.sort((a, b) => (b?.minDeliveryDays || 0) - (a?.minDeliveryDays || 0));
    case SortOptionsEnum.MaxDeliveryAsc:
      return rules.sort((a, b) => (a?.maxDeliveryDays || 0) - (b?.maxDeliveryDays || 0));
    case SortOptionsEnum.MaxDeliveryDesc:
      return rules.sort((a, b) => (b?.maxDeliveryDays || 0) - (a?.maxDeliveryDays || 0));
    default:
      return rules;
  }
};

export const findLanguage = (code: string) => {
  return optionLanguage.find((item) => item.value.toLowerCase() === code.toLowerCase());
};

export function checkIfIsLightColor(color: string) {
  // Hàm chuyển đổi mã màu HEX sang RGB
  const hexToRgb = (hex: string) => {
    const cleanHex = hex.replace('#', '');
    const bigint = parseInt(cleanHex, 16);
    return [
      (bigint >> 16) & 255, // Red
      (bigint >> 8) & 255, // Green
      bigint & 255, // Blue
    ];
  };
  // Hàm tính độ sáng tương đối
  const getLuminance = (r: number, g: number, b: number) => {
    // Chuyển đổi RGB (0-255) sang các giá trị từ 0-1
    const [R, G, B] = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    // Tính độ sáng tương đối
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };
  // Hàm kiểm tra màu là "dark" hay "light"
  const checkColorTheme = (hex: string) => {
    const [r, g, b] = hexToRgb(hex); // Chuyển thành RGB
    const luminance = getLuminance(r, g, b); // Tính độ sáng
    return luminance > 0.5 ? true : false; // So sánh với ngưỡng 0.5
  };

  const theme = checkColorTheme(color);

  return theme;
}

export const INCLUDE_CONDITIONS: Array<ItemConditionEnum> = [
  ItemConditionEnum.IncludeProduct,
  ItemConditionEnum.IncludeCollection,
  ItemConditionEnum.IncludeVendor,
  ItemConditionEnum.IncludeProductTag,
  ItemConditionEnum.IncludeSKU,
  // ItemConditionEnum.IncludeInventoryLocation,
  // ItemConditionEnum.IncludeProductMetafield,
  // ItemConditionEnum.IncludeVariantMetafield,
];

export const EXCLUDE_CONDITIONS: Array<ItemConditionEnum> = [
  ItemConditionEnum.ExcludeProduct,
  ItemConditionEnum.ExcludeCollection,
  ItemConditionEnum.ExcludeVendor,
  ItemConditionEnum.ExcludeProductTag,
  ItemConditionEnum.ExcludeSKU,
  // ItemConditionEnum.ExcludeInventoryLocation,
  // ItemConditionEnum.ExcludeProductMetafield,
  // ItemConditionEnum.ExcludeVariantMetafield,
];

export const checkConditionType = (conditions: Array<{ type: ItemConditionEnum }>, type: AppliedItemTypes): boolean => {
  const targetConditions: Array<ItemConditionEnum> = type === AppliedItemTypes.Include ? INCLUDE_CONDITIONS : EXCLUDE_CONDITIONS;
  return conditions.some((item) => targetConditions.includes(item.type));
};

export const findConditionByType = (data: IItemCondition[], type: [ItemConditionEnum, ItemConditionEnum]): IItemCondition => {
  const [includeType, excludeType] = type;
  const currentData: IItemCondition = data?.find(
    (item) => item.type === includeType || item.type === excludeType,
  ) as IItemCondition;
  return currentData;
};

export const checkIsEmptyRequiredDataToShowException = (data: IItemCondition[]) => {
  const dataVendors = findConditionByType(data, [ItemConditionEnum.IncludeVendor, ItemConditionEnum.ExcludeVendor]);
  const dataCollections = findConditionByType(data, [ItemConditionEnum.IncludeCollection, ItemConditionEnum.ExcludeCollection]);
  const dataProductTags = findConditionByType(data, [ItemConditionEnum.IncludeProductTag, ItemConditionEnum.ExcludeProductTag]);
  // const dataInventoryLocations = findConditionByType(data, [
  //   ItemConditionEnum.IncludeInventoryLocation,
  //   ItemConditionEnum.ExcludeInventoryLocation,
  // ]);
  // const dataProductMetafields = findConditionByType(data, [
  //   ItemConditionEnum.IncludeProductMetafield,
  //   ItemConditionEnum.ExcludeProductMetafield,
  // ]);
  // const dataVariantMetafields = findConditionByType(data, [
  //   ItemConditionEnum.IncludeVariantMetafield,
  //   ItemConditionEnum.ExcludeVariantMetafield,
  // ]);

  const listValues = [
    dataVendors,
    dataCollections,
    dataProductTags,
    // dataInventoryLocations,
    // dataProductMetafields,
    // dataVariantMetafields,
  ].filter((item) => item);

  const isEmptyRequiredData = listValues.every((item) => {
    return item?.value?.filter((item) => item.data)?.length === 0;
  });

  return isEmptyRequiredData;
};
